import { STRUCTURES, golemCount } from '@somnhold/content';
import { addRes, builtMaxHp, event, maxCharges, sortedCellKeys } from './core.ts';
import { creditRaised } from './deeds.ts';
import { crownKeep, districtGolems, raiseDistrict } from './eras.ts';
import { accrue, buildersFor, gatherers, worksGolems } from './golems.ts';
import type { Built, CampaignState, Job, Ms } from './types.ts';
import { completeEarth } from './earth.ts';

export const golemsFor = (s: CampaignState): number =>
  golemCount(s.players.filter((p) => !p.left).length) + (s.extraGolems && s.extraGolems.marea === s.mareaIndex ? s.extraGolems.count : 0) + districtGolems(s) + worksGolems(s);

/** Golems free for jobs: all of them, less the ones the table sends gathering on terraced land. */
export const buildingGolems = (s: CampaignState): number => (s.map.land ? buildersFor(s, golemsFor(s)) : golemsFor(s));

/** Jobs with golems on them right now: funded first, then by id. */
export const activeJobs = (s: CampaignState): { key: string; job: Job }[] => {
  const list: { key: string; job: Job }[] = [];
  for (const key of sortedCellKeys(s)) {
    const j = s.cells[key]?.job;
    if (j && j.stage === 'building') list.push({ key, job: j });
  }
  list.sort((a, b) => a.job.fundedAt - b.job.fundedAt || a.job.id - b.job.id);
  return list.slice(0, buildingGolems(s));
};

export const completeJob = (s: CampaignState, key: string, now: Ms): void => {
  const cell = s.cells[key];
  const j = cell?.job;
  if (!cell || !j) return;
  if (j.kind === 'district') {
    // The Great Work is done: the district rises (if its side still takes one; otherwise it's paid back).
    cell.job = null;
    delete s.cells[key];
    // A crowning (eras IV on) has no district: the keep is restored and the era turns.
    if (!j.district) crownKeep(s, now, j.placedBy);
    else if (!raiseDistrict(s, j.district, now, j.placedBy)) {
      addRes(s.storage, j.paid);
      return;
    }
    creditRaised(s, j.placedBy);
    return;
  }
  // An earthwork changes the land and leaves the cell free.
  if (j.kind === 'earth') {
    completeEarth(s, key, now);
    return;
  }
  if (j.kind === 'new') {
    const b: Built = {
      structure: j.structure,
      level: j.level,
      material: j.material,
      hp: 0,
      charges: STRUCTURES[j.structure].charges ?? 0,
      builtBy: j.placedBy,
      enchant: null,
      fortifiedUntil: 0,
      // A three-cell building keeps the way it was planned to turn: its cells, plinth and place in battle follow it.
      ...(j.turn ? { turn: j.turn } : {}),
    };
    b.hp = builtMaxHp(b, now);
    cell.built = b;
  } else if (j.kind === 'repair' && cell.built) {
    const b = cell.built;
    b.hp = builtMaxHp(b, now);
    if (STRUCTURES[b.structure].charges) b.charges = maxCharges(b);
    cell.job = null;
    event(s, now, 'repaired', { pid: j.placedBy, cellKey: key });
    return;
  } else if (cell.built) {
    const b = cell.built;
    const before = builtMaxHp(b, now);
    if (j.kind === 'level') b.level = j.level;
    else if (j.kind === 'branch' && j.branch) b.branch = j.branch;
    else if (j.kind === 'mount' && j.mount) b.mount = { type: j.mount, level: j.level };
    else b.material = j.material;
    // The new max HP adds on top; damage already taken stays.
    b.hp += builtMaxHp(b, now) - before;
    if (j.kind === 'level' && STRUCTURES[b.structure].charges) b.charges = maxCharges(b);
  }
  cell.job = null;
  creditRaised(s, j.placedBy);
  event(s, now, 'built', { pid: j.placedBy, cellKey: key, data: { s: j.structure, level: j.level, material: j.material } });
};

/**
 * Golems work in real time (SPEC §5.1): each active job gets one golem; when a job finishes the golem
 * moves on. Event-driven, exact, and independent of how often it is called (calling it twice to the same `now` changes nothing).
 */
export const advanceTime = (s: CampaignState, now: Ms): void => {
  if (now <= s.clock) return;
  let t = s.clock;
  let guard = 0;
  while (t < now && guard++ < 10000) {
    const active = activeJobs(s);
    let dt = now - t;
    for (const { job } of active) {
      const rem = job.totalMs - job.progressMs;
      if (rem > 0 && rem < dt) dt = rem;
    }
    for (const { job } of active) job.progressMs += dt;
    // Terraced land: the golems not on a job gather.
    if (s.map.land) accrue(s, gatherers(s, golemsFor(s), active.length), dt);
    t += dt;
    let completed = false;
    for (const { key, job } of active) {
      if (job.progressMs >= job.totalMs) {
        completeJob(s, key, t);
        completed = true;
      }
    }
    if (!completed && active.length === 0 && !s.map.land) break;
  }
  // Conditions that last days wear off.
  for (const p of s.players) if (p.hero.ailment && p.hero.ailment.until <= now) delete p.hero.ailment;
  s.clock = now;
};


/**
 * How long new work of `workMs` would take if it were funded now: right away if a golem is free,
 * otherwise once the first busy golem is done. Materials still missing are not counted.
 */
export const forecastMs = (s: CampaignState, workMs: number): number => {
  const busy = activeJobs(s);
  if (busy.length < buildingGolems(s)) return workMs;
  let first = 0x7fffffff;
  for (const a of busy) {
    const rem = a.job.totalMs - a.job.progressMs;
    if (rem < first) first = rem;
  }
  return (first === 0x7fffffff ? 0 : first) + workMs;
};

export const jobEta = (s: CampaignState, key: string): number => {
  const j = s.cells[key]?.job;
  if (!j || j.stage !== 'building') return -1;
  const active = activeJobs(s).some((a) => a.key === key);
  return active ? j.totalMs - j.progressMs : -1;
};

